import { getErrorDetail } from "components/utility/utils";
import { useUser } from "contexts/UserContext";
import { useCallback, useReducer, useMemo, useEffect } from "react";
import { fetchOrchidAPI } from "utils/api";
import { reportError, reportProblem } from "utils/frontend_error_reporting";
import OrchidDriveFilesReducer from "../reducers/orchidDriveFiles";
import {
  APIOrchidDriveDirectory,
  APIOrchidDriveFile,
  OrchidDriveData,
  OrchidDriveDirectory,
  orchidDriveDirectoryFromAPI,
  OrchidDriveEntryKeys,
  OrchidDriveFile,
  orchidDriveFileFromAPI,
} from "../types";

export type AddPendingDriveEntriesFunc = (
  fileEntries: APIOrchidDriveFile[],
  directoryEntries: APIOrchidDriveDirectory[],
) => void;
export type UpdateAvailableDriveEntriesFunc = (
  fileEntries: OrchidDriveFile[],
  directoryEntries: OrchidDriveDirectory[],
) => void;

type OrchidDriveCollectParentDirectoriesReducerUpdateAction = {
  type: "directories_to_fetch_update";
  directoriesNeeded: Set<string>;
};

type OrchidDriveCollectParentDirectoriesReducerFailedToFetchAction = {
  type: "failed_to_fetch";
  timestamp: number;
  entriesFailed: string[];
};

type OrchidDriveCollectParentDirectoriesReducerCleanupOldFailedAction = {
  type: "cleanup_old_failed";
  timestamp: number;
};

export default function OrchidDriveCollectParentDirectoriesReducer(
  state: {
    directoriesBeingFetched: string[];
    directoriesFailedToFetch: Map<string, number>;
  },
  action:
    | OrchidDriveCollectParentDirectoriesReducerUpdateAction
    | OrchidDriveCollectParentDirectoriesReducerFailedToFetchAction
    | OrchidDriveCollectParentDirectoriesReducerCleanupOldFailedAction,
) {
  switch (action.type) {
    case "directories_to_fetch_update": {
      if (
        state.directoriesBeingFetched.some(
          (e) => !action.directoriesNeeded.has(e),
        )
      )
        return state;
      return {
        ...state,
        directoriesBeingFetched: Array.from(action.directoriesNeeded).slice(
          0,
          100,
        ),
      };
    }
    case "failed_to_fetch": {
      const directoriesFailedToFetch = new Map(state.directoriesFailedToFetch);
      for (const entry of action.entriesFailed) {
        directoriesFailedToFetch.set(entry, action.timestamp);
      }
      return {
        ...state,
        directoriesFailedToFetch,
      };
    }
    case "cleanup_old_failed": {
      const directoriesFailedToFetch = new Map();
      for (const [
        directoryId,
        timestamp,
      ] of state.directoriesFailedToFetch.entries()) {
        if (timestamp > action.timestamp) {
          directoriesFailedToFetch.set(directoryId, timestamp);
        }
      }
      return {
        ...state,
        directoriesFailedToFetch,
      };
    }
  }
  throw new Error(`unexpected action type: ${action}`);
}

type OrchidDriveFileWithKeys = APIOrchidDriveFile & {
  keys: OrchidDriveEntryKeys;
};

type OrchidDriveDirectoryWithKeys = APIOrchidDriveDirectory & {
  keys: OrchidDriveEntryKeys;
};

export function useOrchidDriveData(
  entriesNeedKeys,
  orchidDriveEntryKeys,
): {
  addPendingDriveEntries: AddPendingDriveEntriesFunc;
  updateAvailableDriveEntries: UpdateAvailableDriveEntriesFunc;
  orchidDriveFiles: OrchidDriveData;
} {
  const { user } = useUser();
  const userSub = user?.sub;

  const [
    { pending: pendingDriveEntries, available: orchidDriveFiles },
    orchidDriveFilesDispatch,
  ] = useReducer(OrchidDriveFilesReducer, {
    pending: { files: {}, directories: {} },
    available: { files: {}, directories: {} },
  });
  useEffect(() => orchidDriveFilesDispatch({ type: "reset" }), [userSub]);

  const { entriesWithMissingKeys, entriesWithKeysAvailable } = useMemo(() => {
    const missingKeys: string[] = [];
    const fileEntries: OrchidDriveFileWithKeys[] = [];
    const directoryEntries: OrchidDriveDirectoryWithKeys[] = [];

    for (const fileEntry of Object.values(pendingDriveEntries.files)) {
      const keys = orchidDriveEntryKeys[fileEntry.file_data_id];
      if (keys) {
        fileEntries.push({ ...fileEntry, keys });
      } else {
        missingKeys.push(fileEntry.file_data_id);
      }
    }
    for (const directoryEntry of Object.values(
      pendingDriveEntries.directories,
    )) {
      const keys = orchidDriveEntryKeys[directoryEntry.directory_id];
      if (orchidDriveEntryKeys[directoryEntry.directory_id]) {
        directoryEntries.push({ ...directoryEntry, keys });
      } else {
        missingKeys.push(directoryEntry.directory_id);
      }
    }

    return {
      entriesWithMissingKeys: missingKeys,
      entriesWithKeysAvailable: {
        fileEntries,
        directoryEntries,
      },
    };
  }, [pendingDriveEntries, orchidDriveEntryKeys]);

  useEffect(() => {
    if (entriesWithMissingKeys.length > 0) {
      entriesNeedKeys(entriesWithMissingKeys);
    }
  }, [entriesNeedKeys, entriesWithMissingKeys]);

  useEffect(() => {
    let isCancelled = false;

    (async function () {
      const fileEntries: OrchidDriveFile[] = [];
      const directoryEntries: OrchidDriveDirectory[] = [];
      for (const fileEntry of entriesWithKeysAvailable.fileEntries) {
        try {
          fileEntries.push(
            await orchidDriveFileFromAPI(
              fileEntry,
              fileEntry.keys.metaDataCipherKey,
            ),
          );
        } catch (err: any) {
          reportError({
            reporter_module: "orchidDrive",
            context: {
              func: "useOrchidDriveData",
              error: "failed to decode file entry",
              entryId: fileEntry.file_data_id,
            },
            err,
          });
        }
        if (isCancelled) return;
      }
      for (const directoryEntry of entriesWithKeysAvailable.directoryEntries) {
        try {
          directoryEntries.push(
            await orchidDriveDirectoryFromAPI(
              directoryEntry,
              directoryEntry.keys.metaDataCipherKey,
            ),
          );
        } catch (err) {
          reportProblem({
            reporter_module: "orchidDrive",
            context: {
              func: "useOrchidDriveData",
              entryId: directoryEntry.directory_id,
            },
            frontend_error: `failed to decode directory entry`,
          });
        }
        if (isCancelled) return;
      }
      if (isCancelled) return;
      orchidDriveFilesDispatch({
        type: "update_available",
        fileUpdates: fileEntries,
        directoryUpdates: directoryEntries,
      });
    })();
    return () => {
      isCancelled = true;
    };
  }, [entriesWithKeysAvailable]);

  const [
    { directoriesBeingFetched, directoriesFailedToFetch },
    collectParentDirectoriesDispatch,
  ] = useReducer(OrchidDriveCollectParentDirectoriesReducer, {
    directoriesBeingFetched: [],
    directoriesFailedToFetch: new Map(),
  });

  const parentDirectoriesToGet = useMemo(() => {
    const directoryIds = new Set(
      Object.keys(pendingDriveEntries.directories).concat(
        Object.keys(orchidDriveFiles.directories),
      ),
    );
    return new Set(
      Object.values(orchidDriveFiles.files)
        .filter(
          (e) =>
            e.parentDirectoryId !== null &&
            !directoryIds.has(e.parentDirectoryId),
        )
        .map((e) => e.fileDataId)
        .concat(
          Object.values(orchidDriveFiles.directories)
            .filter(
              (e) =>
                e.parentDirectoryId !== null &&
                !directoryIds.has(e.parentDirectoryId),
            )
            .map((e) => e.directoryId),
        )
        .filter((e) => !directoriesFailedToFetch.has(e)),
    );
  }, [pendingDriveEntries, orchidDriveFiles, directoriesFailedToFetch]);

  useEffect(() => {
    collectParentDirectoriesDispatch({
      type: "directories_to_fetch_update",
      directoriesNeeded: parentDirectoriesToGet,
    });
  }, [parentDirectoriesToGet]);

  useEffect(() => {
    if (directoriesBeingFetched.length === 0) return;

    let abortController: AbortController = new AbortController();

    (async function () {
      try {
        const response = await fetchOrchidAPI(
          "/api/drive_data/snapshot/storage",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ entry_ids: directoriesBeingFetched }),
            signal: abortController ? abortController.signal : null,
          },
        );
        if (!response.ok) {
          const errorText = await response.text();
          // TODO: should be increasing backoff time
          collectParentDirectoriesDispatch({
            type: "failed_to_fetch",
            timestamp: new Date().getTime() + 10 * 1000,
            entriesFailed: directoriesBeingFetched,
          });
          const errorDetails = getErrorDetail(errorText);
          await reportProblem({
            reporter_module: "useOrchidDriveData",
            context: { func: "fetch parent directories", errorDetails },
            frontend_error: `request failed: ${errorDetails}`,
          });
          return;
        }

        const resultData = await response.json();

        if (resultData.failed_entries.length > 0) {
          // It's odd parent directories failed, let's see if the problem
          // is resolved in 3 min
          collectParentDirectoriesDispatch({
            type: "failed_to_fetch",
            timestamp: new Date().getTime() + 3 * 60 * 1000,
            entriesFailed: resultData.failed_entries.map((e) => e.entry_id),
          });
        }

        orchidDriveFilesDispatch({
          type: "add_pending",
          files: resultData.files,
          directories: resultData.directories,
        });
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this fetch was cancelled
          return;
        }
        collectParentDirectoriesDispatch({
          type: "failed_to_fetch",
          timestamp: new Date().getTime() + 3 * 60 * 1000,
          entriesFailed: directoriesBeingFetched,
        });
        await reportError({
          reporter_module: "useOrchidDriveData",
          context: { func: "fetch parent directories" },
          err,
        });
      }
    })();

    return () => {
      if (abortController) abortController.abort();
    };
  }, [directoriesBeingFetched]);

  const nextRetryUnixTimestamp: number | null = useMemo(() => {
    if (directoriesFailedToFetch.size === 0) return null;
    const timestamps: number[] = Array.from(directoriesFailedToFetch.values());
    return Math.min(...timestamps);
  }, [directoriesFailedToFetch]);

  // retry failed entries
  useEffect(() => {
    if (nextRetryUnixTimestamp === null) return;

    const timeToWait = Math.max(
      0,
      nextRetryUnixTimestamp - new Date().getTime(),
    );

    const timer = setTimeout(() => {
      collectParentDirectoriesDispatch({
        type: "cleanup_old_failed",
        timestamp: nextRetryUnixTimestamp,
      });
    }, timeToWait);
    return () => clearTimeout(timer);
  }, [nextRetryUnixTimestamp]);

  const addPendingDriveEntries = useCallback(
    (
      fileEntries: APIOrchidDriveFile[],
      directoryEntries: APIOrchidDriveDirectory[],
    ) => {
      orchidDriveFilesDispatch({
        type: "add_pending",
        files: fileEntries,
        directories: directoryEntries,
      });
    },
    [],
  );

  const updateAvailableDriveEntries = useCallback(
    (
      fileEntries: OrchidDriveFile[],
      directoryEntries: OrchidDriveDirectory[],
    ) => {
      orchidDriveFilesDispatch({
        type: "update_available",
        fileUpdates: fileEntries,
        directoryUpdates: directoryEntries,
      });
    },
    [],
  );

  return {
    addPendingDriveEntries,
    updateAvailableDriveEntries,
    orchidDriveFiles,
  };
}
